import React from 'react';
import { Typography, Button, App } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import CrudPage from '../../../components/common/CrudPage';
import { horoscopService } from '../../../services/cms/contentManagementService';

const { Text } = Typography;

const QUERY_KEY = 'horoscopList';

// Thứ tự + nhãn theo đúng popup của CMS cũ (backend/horoscop/form.blade.php).
// CMS cũ KHÔNG có trường Ưu tiên / Public trong popup nên ở đây cũng bỏ.
//
// Bắt buộc theo backend: 9 cột nội dung dưới đây là NOT NULL không default ở bảng `horoscop`
// (bỏ trống -> HTTP 500, QA-02), và `date` đã được thêm required vào HoroscopVal (QA-03).
// Chỉ `soMayMan` là cột cho phép rỗng. RenderForm tự sinh thông báo "<Nhãn> không được bỏ trống".
const REQUIRED = [{ required: true }];

const formFields = [
  { name: 'dateTitle', label: 'Tiêu đề ngày', type: 'input', rules: REQUIRED },
  // Ô ngày trước đây là input chữ tự do: gõ '10-09-2020' là 500 `Incorrect date value` (QA-03)
  { name: 'date', label: 'Ngày phát hành', type: 'date', format: 'DD/MM/YYYY', submitFormat: 'YYYY-MM-DD', rules: REQUIRED },
  { name: 'tenGiap', label: 'Tên giáp', type: 'input', rules: REQUIRED },
  { name: 'congViec', label: 'Công việc', type: 'textarea', propsInput: { rows: 2 }, rules: REQUIRED },
  { name: 'tinhCam', label: 'Tình cảm', type: 'textarea', propsInput: { rows: 2 }, rules: REQUIRED },
  { name: 'taiVan', label: 'Tài vận', type: 'textarea', propsInput: { rows: 2 }, rules: REQUIRED },
  { name: 'canTrong', label: 'Cẩn trọng', type: 'textarea', propsInput: { rows: 2 }, rules: REQUIRED },
  { name: 'mauMayMan', label: 'Màu sắc vượng vận', type: 'input', rules: REQUIRED },
  { name: 'quyNhan', label: 'Quý nhân', type: 'input', rules: REQUIRED },
  { name: 'soMayMan', label: 'Số may mắn', type: 'input' },
  { name: 'gioTot', label: 'Giờ tốt', type: 'input', rules: REQUIRED },
];

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: 'TÊN GIÁP', dataIndex: 'tenGiap', key: 'tenGiap', width: 150, render: (t) => <Text strong>{t}</Text> },
  { title: 'NGÀY', dataIndex: 'dateTitle', key: 'dateTitle', width: 150 },
  { title: 'CÔNG VIỆC', dataIndex: 'congViec', key: 'congViec', ellipsis: true, width: 200 },
  { title: 'TÌNH CẢM', dataIndex: 'tinhCam', key: 'tinhCam', ellipsis: true, width: 200 },
  { title: 'TÀI VẬN', dataIndex: 'taiVan', key: 'taiVan', ellipsis: true, width: 200 },
  { title: 'NGƯỜI TẠO', dataIndex: 'author_name', key: 'author_name', width: 140, render: (val, r) => val || r.create_by || r.created_by || '-' },
  { title: 'CẬP NHẬT', dataIndex: 'updated_at', key: 'updated_at', width: 140, render: (t) => t ? dayjs(t).format('DD/MM/YYYY HH:mm') : '-' },
];

const filterFields = [
  {
    label: 'Ngày',
    value: 'date',
    type: 'date',
    // Cột horoscop.date kiểu DATE, CMS cũ so `=` theo 'YYYY-MM-DD'
    submitFormat: 'YYYY-MM-DD',
  },
];

const QuanLyTuVi = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  // "Lấy lại dữ liệu": backend gọi API nguồn rồi ghi đè bản ghi tử vi của ngày hôm nay
  const resetMutation = useMutation({
    mutationFn: horoscopService.resetData,
    onSuccess: (res) => {
      message.success(res?.message || 'Đã lấy lại dữ liệu tử vi');
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      // Interceptor axios đã hiện thông báo cho lỗi 400; fallback cho trường hợp khác
      const msg = error?.data?.message || error?.message;
      if (msg) message.error(msg);
    },
  });

  const headerActions = (
    <Button icon={<SyncOutlined />} loading={resetMutation.isPending} onClick={() => resetMutation.mutate()}>
      Lấy lại dữ liệu
    </Button>
  );

  return (
    <CrudPage
      title="QUẢN LÝ TỬ VI"
      subTitle="Tử vi hàng ngày"
      itemsName="Tử vi"
      queryKey={QUERY_KEY}
      service={horoscopService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm theo tên giáp..."
      addButtonLabel="Tạo mới"
      renderHeaderActions={headerActions}
      formConfig={{ width: 900 }}
    />
  );
};

export default QuanLyTuVi;
